import React, { useState } from "react";
import { useFieldArray, Controller } from "react-hook-form";

import {
  Grid,
  TextField,
  Typography,
  Paper,
  Link,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import axios from "../../../../../axios";
import moment from "moment";

import CloseIcon from "@material-ui/icons/Close";
import EmailHistory from "./EmailHistory";

const ReferenceDetails = ({ control, recId, recName, username }) => {
  const { fields } = useFieldArray({
    control,
    name: "referenceResponses",
  });

  const [sendingIndex, setSendingIndex] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [sentIndexes, setSentIndexes] = useState([]); // ✅ per row

  const handleOpenResponse = (responseData) => {
    setSelectedResponse(responseData);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedResponse(null);
  };

  const sendReferenceEmail = async (email, refName, id, index) => {
    if (!email || !refName) {
      setSnackbar({
        open: true,
        message: "Email address and name are required",
        severity: "warning",
      });
      return;
    }

    try {
      setSendingIndex(index);

      await axios.post("/api/v1/recruitments/send-reference-confirmation", {
        email,
        recName,
        refName,
        id,
        recId,
        sentBy: username,
      });

      setSentIndexes((prev) => [...prev, index]);

      setSnackbar({
        open: true,
        message: "Reference email sent successfully",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to send email",
        severity: "error",
      });
    } finally {
      setSendingIndex(null);
    }
  };

  return (
    <Paper variant="outlined" style={{ padding: "20px", margin: "20px 0" }}>
      <Typography variant="h6" gutterBottom>
        Reference Details
      </Typography>

      {fields.slice(0, 3).map((item, index) => (
        <Paper
          variant="outlined"
          key={item._id || item.id || index}
          style={{ padding: "16px", marginBottom: "16px" }}
        >
          {/* Hidden ID */}
          <Controller
            name={`referenceResponses[${index}]._id`}
            control={control}
            defaultValue={item._id || ""}
            render={({ value, onChange }) => (
              <input
                type="hidden"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
          />
          <Typography variant="subtitle1" gutterBottom>
            Reference {index + 1}
          </Typography>
          <Grid container spacing={2}>
            {[
              { name: "refName", label: "Name" },
              { name: "refPosition", label: "Position" },
              { name: "refDepartment", label: "Department" },
              { name: "refCompany", label: "Company" },
              { name: "refEmail", label: "Email" },
              { name: "refContactNumber", label: "Contact Number" },
            ].map((fieldItem) => (
              <Grid item xs={12} md={4} key={fieldItem.name}>
                <Controller
                  name={`referenceResponses[${index}].${fieldItem.name}`}
                  control={control}
                  defaultValue={item[fieldItem.name] || ""}
                  render={({ value, onChange }) => (
                    <TextField
                      value={value || ""}
                      onChange={(e) => onChange(e.target.value)}
                      label={fieldItem.label}
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Grid>
            ))}
          </Grid>
          {/* Send Email */}
          {item.responseStatus === "Pending" &&
            !sentIndexes.includes(index) && (
              <Link
                component="button"
                variant="body2"
                style={{ marginTop: 12 }}
                disabled={sendingIndex === index}
                onClick={(e) => {
                  e.preventDefault();
                  sendReferenceEmail(
                    item.refEmail,
                    item.refName,
                    item._id,
                    index,
                  );
                }}
              >
                {sendingIndex === index ? "Sending..." : "Send Reference Email"}
              </Link>
            )}
          {/* <span>Response Status: {item.responseStatus}</span>
          <span>Reference Sent At: {item.referenceConfirmationSentAt}</span>
          <span>Submitted At: {item.submittedAt}</span> */}
          {item.responseStatus === "Awaiting Response" && (
            <>
              <EmailHistory item={item} />

              <Link
                component="button"
                variant="body2"
                style={{ marginTop: 8, display: "block" }}
                disabled={sendingIndex === index}
                onClick={(e) => {
                  e.preventDefault();

                  sendReferenceEmail(
                    item.refEmail,
                    item.refName,
                    item._id,
                    index,
                  );
                }}
              >
                {sendingIndex === index
                  ? "Resending..."
                  : "Resend Reference Email"}
              </Link>
            </>
          )}

          {item.responseStatus === "Submitted" && (
            <>
              <EmailHistory item={item} />
              {item?.submittedAt && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginBottom: 0, fontWeight: 500 }}
                >
                  <strong>Response submitted:</strong>{" "}
                  {moment(item.submittedAt).format("DD MMM YYYY [at] hh:mm A")}
                </Typography>
              )}
              <Link
                component="button"
                variant="body2"
                style={{ marginTop: 12 }}
                onClick={(e) => {
                  e.preventDefault();
                  handleOpenResponse(item.responseData);
                }}
              >
                View Response
              </Link>
            </>
          )}
        </Paper>
      ))}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Interpreter Reference Form Response
          <IconButton
            style={{ position: "absolute", right: 10, top: 10 }}
            onClick={handleCloseDialog}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {selectedResponse && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Interpreter / Translator Name"
                  value={
                    selectedResponse.refName ||
                    selectedResponse.interpreterTranslatorName ||
                    ""
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Languages they cover"
                  value={selectedResponse.languagesCovered || ""}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Dialects they cover"
                  value={selectedResponse.dialectsCovered || ""}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="In what capacity do you know this person?"
                  value={selectedResponse.capacityKnown || ""}
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Comment about their ability as interpreter / translator"
                  value={selectedResponse.commentonAbility || ""}
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="How long have they been assisting your agency / firm?"
                  value={selectedResponse.durationAssisting || ""}
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="What services have they provided?"
                  value={selectedResponse.serviceTypesProvided || ""}
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Legal / Medical / Public interpreting?"
                  value={selectedResponse.assistedLegalMedicalPublic || ""}
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Translation / transcription work?"
                  value={selectedResponse.translationOrTranscriptionWork || ""}
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Interpreted for court?"
                  value={selectedResponse.assistedForCourtPurposes || ""}
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Overall interpreting hours"
                  value={selectedResponse.overallInterpretingHours || ""}
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Other comments"
                  value={selectedResponse.otherComments || ""}
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              {/* Divider style heading */}
              <Grid item xs={12}>
                <Typography variant="h6">Reference Details</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Your Name"
                  value={selectedResponse.yourName || ""}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Position"
                  value={selectedResponse.position || ""}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Department"
                  value={selectedResponse.department || ""}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Company Name"
                  value={selectedResponse.companyName || ""}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Email Address"
                  value={selectedResponse.emailAddress || ""}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Contact Number"
                  value={selectedResponse.contactNumber || ""}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

export default ReferenceDetails;
